import React, {useCallback, useEffect, useMemo, useState} from 'react';
import DocumentTitle from 'react-document-title';

import isEmpty from 'lodash.isempty';
import {InternalServerError} from 'src/components/page-not-found';
import {useFilters} from 'src/hooks/useFilters';
import {usePagination} from 'src/hooks/usePagination';
import {useSortingRows} from 'src/hooks/useSortingRows';
import {AttributesExcelDownload} from 'src/modules/attributes/download/attributes-excel-download';
import {columnCriteriaStripeData} from 'src/modules/attributes/filter-utils/criteriaStripeData';
import {getAttributesInfo} from 'src/modules/attributes/services';
import {getAttributesFilters} from 'src/modules/attributes/services/filters';
import {
    clearAttributesFilters,
    setAllAttributesFilters,
    setAttributesFilter,
} from 'src/modules/attributes/services/filters/actions';
import {getFilteredAttributes} from 'src/modules/attributes/services/selectors';
import {getEventsInfo} from 'src/modules/events/services';
import {
    getAllAttributeRelationInfo,
    getAllAttributeRelationInfoFailure,
    getAllAttributeRelationInfoSuccess,
} from 'src/openapi/apis/AttributeControllerApi';
import {AttributeRelationInfo} from 'src/openapi/models/AttributeRelationInfo';
import {useAppDispatch, useAppSelector} from 'src/store';
import {createColumnCriteria} from 'src/utils/criteriaStripeDataHelper';

import {Loading} from '@pb/kit-component-loading';

import {ColumnTypes} from './constants';
import {Filters, SortColumns} from './typedefs';
import {AttributesView} from './views/attributes.view';

export const AttributesController = () => {
    const dispatch = useAppDispatch();
    const {filters} = useFilters<Filters>(getAttributesFilters, clearAttributesFilters, setAllAttributesFilters);
    const rows = useAppSelector(getAttributesInfo);
    const eventsInfo = useAppSelector(getEventsInfo);
    const [error, setError] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const filteredRows = useAppSelector(getFilteredAttributes);
    const {sortConfig, onSort, sortedRows} = useSortingRows<AttributeRelationInfo, SortColumns>(filteredRows);
    const {slicedRowsByPage, intersectRef, isVisible, hasMore} = usePagination<AttributeRelationInfo>(sortedRows);

    const clearAllFilters = useCallback(() => {
        dispatch(clearAttributesFilters());
    }, [dispatch]);

    useEffect(() => {
        if (isEmpty(rows)) {
            setIsLoading(true);
            dispatch(getAllAttributeRelationInfo()).then(e => {
                if (e.type === getAllAttributeRelationInfoSuccess.type) {
                    setIsLoading(false);
                }
                if (e.type === getAllAttributeRelationInfoFailure.type) {
                    setError(true);
                    setIsLoading(false);
                }
            });
        }
    }, [dispatch, eventsInfo, rows]);

    const setFilter = useCallback((filterType: string, filterValue: string[]) => {
        dispatch(setAttributesFilter({filterType, filterValue}));
    }, [dispatch]);

    const onPillRemove = useCallback((pillName: string, column: ColumnTypes) => {
        setFilter(column, filters[column].filter(value => value !== pillName));
    }, [filters, setFilter]);

    const criteriaStripeData = useMemo(() => {
        return [...createColumnCriteria(filters, columnCriteriaStripeData, onPillRemove)];
    }, [filters, onPillRemove]);

    if (error) {
        return (
            <InternalServerError/>
        );
    }

    if (isEmpty(rows) && isLoading) {
        return (
            <Loading fast={true}/>
        );
    }

    return (
        <>
            <DocumentTitle title="Attributes"/>
            <AttributesView {...{
                rows,
                setFilter,
                onSort,
                sortConfig,
                preparedRows: slicedRowsByPage,
                filters,
                clearAllFilters,
                criteriaStripeData,
                intersectRef,
                isVisible,
                hasMore,
            }}
            />
            <AttributesExcelDownload filters={filters} attributes={sortedRows}/>
        </>
    );
};
